import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil } from 'lucide-react';
import { StaffApis } from 'apis/staff';
import PageContainer from 'ui/PageContainer';
import PageHeader from 'ui/PageHeader';
import Section from 'ui/Section';
import Button from 'ui/Button';
import Loading from 'ui/Loading';
import Input from 'ui/Input';
import Select from 'ui/Select';
import { Avatar, AvatarFallback, Badge } from 'ui/shadcn';
import { toast } from 'react-toastify';
import { formatPhoneNumber } from 'utils/format';

const POSITION_OPTIONS = ['사원', '대리', '과장', '차장', '부장', '이사', '상무', '고문', '부사장', '대표이사'];

interface UserInfo {
    name: string;
    id: number;
    userId: string;
    type: string;
    email?: string;
    phone?: string;
    department?: string;
    position?: string;
    joinDate?: string;
    lastLogin?: string;
}

/** 읽기 전용 key:value 그리드 — 라벨 xs muted / 값 md foreground, 행 구분은 여백으로 */
const FIELD_GRID = 'grid grid-cols-2 gap-x-6 gap-y-4 lg:grid-cols-4';

const InfoField: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
    <div className="min-w-0">
        <dt className="text-xs text-muted-foreground">{label}</dt>
        <dd className="mt-1 truncate text-md text-foreground">{value || '-'}</dd>
    </div>
);

const MyPage = () => {
    const navigate = useNavigate();
    const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
    const [loading, setLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editForm, setEditForm] = useState({
        email: '',
        phone_number: '',
        department: '',
        position: '',
    });
    const [rawResponse, setRawResponse] = useState<any>(null);

    useEffect(() => {
        fetchUserInfo();
    }, []);

    const fetchUserInfo = async () => {
        try {
            const response = await StaffApis.getMyInfo();
            setRawResponse(response);
            setUserInfo({
                name: response.name,
                id: response.token.id,
                userId: response.user_id || response.token.data?.user_id || response.token.id,
                type: response.token.type,
                email: response.email || '-',
                phone: response.phone_number || '-',
                department: response.department || '-',
                position: response.position || '-',
                joinDate: response.created_at ? new Date(response.created_at).toLocaleDateString('ko-KR') : '-',
                lastLogin: new Date().toLocaleString('ko-KR')
            });
            setEditForm({
                email: response.email || '',
                phone_number: response.phone_number || '',
                department: response.department || '',
                position: response.position || '',
            });
        } catch (error) {
            console.error('Failed to fetch user info:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = () => {
        setIsEditing(true);
    };

    const handleCancel = () => {
        setIsEditing(false);
        setEditForm({
            email: rawResponse?.email || '',
            phone_number: rawResponse?.phone_number || '',
            department: rawResponse?.department || '',
            position: rawResponse?.position || '',
        });
    };

    const handleSave = async () => {
        if (!userInfo) return;
        try {
            setSaving(true);
            await StaffApis.updateMyProfile(userInfo.id, editForm);
            toast.success('정보가 수정되었습니다.');
            setIsEditing(false);
            await fetchUserInfo();
        } catch (error) {
            console.error('Failed to update profile:', error);
            toast.error('정보 수정에 실패했습니다.');
        } finally {
            setSaving(false);
        }
    };

    const handleLogout = async () => {
        try {
            await StaffApis.logout();
            navigate('/login');
        } catch (error) {
            console.error('Logout failed:', error);
        }
    };

    const handlePasswordReset = () => {
        navigate('/password-reset');
    };

    if (loading) {
        return (
            <PageContainer>
                <Loading text="사용자 정보를 불러오는 중..." />
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            <PageHeader
                title="마이페이지"
                subtitle="개인 정보 및 활동 내역을 확인하세요"
            />

            <div className="space-y-4">
                {/* 프로필 카드 */}
                <Section>
                    <div className="flex flex-wrap items-center justify-between gap-4">
                        <div className="flex min-w-0 items-center gap-4">
                            <Avatar className="h-14 w-14">
                                <AvatarFallback className="bg-primary text-xl text-primary-foreground">
                                    {userInfo?.name?.[0] || 'U'}
                                </AvatarFallback>
                            </Avatar>
                            <div className="min-w-0">
                                <h2 className="truncate text-lg font-semibold text-foreground">
                                    {userInfo?.name}
                                </h2>
                                <div className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
                                    <span>{userInfo?.userId}</span>
                                    {userInfo?.type && <Badge variant="default">{userInfo.type}</Badge>}
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="small" onClick={handlePasswordReset}>
                                비밀번호 변경
                            </Button>
                            <Button variant="ghost" size="small" onClick={handleLogout}>
                                로그아웃
                            </Button>
                        </div>
                    </div>
                </Section>

                {/* 계정 정보 (읽기전용) */}
                <Section title="계정 정보">
                    <dl className={FIELD_GRID}>
                        <InfoField label="이름" value={userInfo?.name} />
                        <InfoField label="사용자 ID" value={userInfo?.userId} />
                        <InfoField label="권한" value={userInfo?.type} />
                        <InfoField label="가입일" value={userInfo?.joinDate} />
                    </dl>
                </Section>

                {/* 프로필 정보 (수정가능) */}
                <Section
                    title="프로필 정보"
                    actions={
                        !isEditing ? (
                            <Button variant="outline" size="small" icon={<Pencil />} onClick={handleEdit}>
                                수정
                            </Button>
                        ) : (
                            <>
                                <Button variant="outline" size="small" onClick={handleCancel}>
                                    취소
                                </Button>
                                <Button variant="primary" size="small" loading={saving} onClick={handleSave}>
                                    저장
                                </Button>
                            </>
                        )
                    }
                >
                    {isEditing ? (
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                            <Input
                                label="이메일"
                                type="email"
                                value={editForm.email}
                                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                                placeholder="이메일 입력"
                            />
                            <Input
                                label="전화번호"
                                type="text"
                                value={editForm.phone_number}
                                onChange={(e) => setEditForm({ ...editForm, phone_number: formatPhoneNumber(e.target.value) })}
                                placeholder="전화번호 입력"
                            />
                            <Input
                                label="부서"
                                type="text"
                                value={editForm.department}
                                onChange={(e) => setEditForm({ ...editForm, department: e.target.value })}
                                placeholder="부서 입력"
                            />
                            <Select
                                label="직급"
                                value={editForm.position}
                                onChange={(e) => setEditForm({ ...editForm, position: e.target.value })}
                                options={POSITION_OPTIONS.map(opt => ({ value: opt, label: opt }))}
                                placeholder="선택"
                            />
                        </div>
                    ) : (
                        <dl className={FIELD_GRID}>
                            <InfoField label="이메일" value={userInfo?.email} />
                            <InfoField label="전화번호" value={userInfo?.phone} />
                            <InfoField label="부서" value={userInfo?.department} />
                            <InfoField label="직급" value={userInfo?.position} />
                        </dl>
                    )}
                </Section>
            </div>
        </PageContainer>
    );
};

export default MyPage;
